import { Coordinates } from '../types/geolocation.types';

const GEOCODING_PERMISSION_TIMEOUT = 30_000;
const GEOCODING_MAX_AGE = 60_000;

export function isQtWebEngine(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /QtWebEngine|Qt\//i.test(navigator.userAgent) ||
    (typeof window !== 'undefined' && 'qt' in window)
  );
}

export function getChromeVersion(): number | null {
  if (typeof navigator === 'undefined') return null;
  const match = navigator.userAgent.match(/(?:Chrome|Chromium|CriOS)\/(\d+)\./);
  return match ? parseInt(match[1], 10) : null;
}

export function isChromium87OrOlder(): boolean {
  const version = getChromeVersion();
  return version !== null && version <= 87;
}

export function isGeolocationSupported(): boolean {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return false;
  }
  if (isQtWebEngine() || isChromium87OrOlder()) {
    return false;
  }
  return true;
}

export async function getGeolocationPermissionState(): Promise<
  PermissionState | 'unsupported' | null
> {
  if (!isGeolocationSupported()) {
    return 'unsupported';
  }

  if (!navigator.permissions) {
    return null;
  }

  try {
    const result = await navigator.permissions.query({
      name: 'geolocation',
    });

    return result.state;
  } catch {
    return null;
  }
}

export function subscribeToGeolocationPermission(
  callback: (state: PermissionState) => void
): () => void {
  if (typeof navigator === 'undefined' || !navigator.permissions || !isGeolocationSupported()) {
    return () => {};
  }

  let isMounted = true;
  let permissionStatus: PermissionStatus | null = null;

  const handleChange = () => {
    if (permissionStatus && isMounted) {
      callback(permissionStatus.state);
    }
  };

  navigator.permissions
    .query({ name: 'geolocation' })
    .then((status) => {
      if (!isMounted) return;
      permissionStatus = status;
      callback(status.state);
      if (typeof status.addEventListener === 'function') {
        status.addEventListener('change', handleChange);
      } else {
        status.onchange = handleChange;
      }
    })
    .catch(() => {
      // Ignore query errors
    });

  return () => {
    isMounted = false;
    if (permissionStatus) {
      if (typeof permissionStatus.removeEventListener === 'function') {
        permissionStatus.removeEventListener('change', handleChange);
      } else {
        permissionStatus.onchange = null;
      }
    }
  };
}

export function getCurrentPosition(): Promise<Coordinates> {
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject(new Error('Geolocation is not supported in this environment'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      reject,
      {
        enableHighAccuracy: false,
        timeout: GEOCODING_PERMISSION_TIMEOUT,
        maximumAge: GEOCODING_MAX_AGE,
      }
    );
  });
}
